/**
 * Borrar un producto: qué se pierde y cuánta fricción corresponde.
 *
 * POR QUÉ EXISTE ESTA DECISIÓN Y NO UN "¿Estás seguro?": borrar un producto en este sistema arrastra
 * DIECISÉIS tablas en cascada. Entre ellas, tres que el vendedor no asocia con "el producto":
 *
 *   · `inventory_movements` — el historial de stock completo, que es contabilidad.
 *   · `meli_item_links` / `falabella_listings` — el vínculo con la publicación del marketplace.
 *   · `product_attributes` — la ficha técnica entera.
 *
 * Y una que SÍ sobrevive, aunque no se note: `order_lines` guarda una COPIA del nombre y del precio
 * y no tiene clave foránea al producto. O sea que el pedido viejo sigue mostrando qué se vendió y a
 * cuánto — pero su `product_id` queda apuntando a la nada, y cualquier reporte que agrupe por
 * producto pierde esas ventas.
 *
 * De ahí el criterio: **la fricción sigue a la consecuencia**. Un producto que nunca se vendió ni se
 * publicó se borra con un clic. Uno con historial exige escribir su nombre — el mismo recurso que
 * usan GitHub o Stripe para lo irreversible, y por la misma razón: obliga a leer qué se está
 * borrando en vez de apretar "sí" de memoria.
 */

export interface DependenciasProducto {
  /** Líneas de pedido que lo referencian: ventas reales. */
  ventas: number;
  /** Publicaciones vivas en marketplaces (Mercado Libre + Falabella). */
  publicaciones: number;
  /** Movimientos de inventario registrados. */
  movimientos: number;
}

export interface VeredictoBorrado {
  /** 'libre' = no se pierde nada que importe · 'con_historial' = se destruye información. */
  nivel: 'libre' | 'con_historial';
  /** Qué se pierde, en palabras del vendedor. Vacío cuando no se pierde nada. */
  consecuencias: string[];
  /** Con historial, la confirmación pide escribir el nombre exacto. */
  exigeEscribirNombre: boolean;
}

const plural = (n: number, singular: string, plural_: string) =>
  `${n} ${n === 1 ? singular : plural_}`;

/**
 * Traduce las dependencias a consecuencias entendibles y decide cuánta fricción pedir.
 *
 * Es pura a propósito: la decisión de "esto es grave" no puede depender de si una consulta llegó a
 * tiempo. Quien la llama junta los números; acá solo se decide.
 */
export function evaluarBorrado(dep: DependenciasProducto): VeredictoBorrado {
  const consecuencias: string[] = [];

  if (dep.ventas > 0) {
    consecuencias.push(
      `Tiene ${plural(dep.ventas, 'venta registrada', 'ventas registradas')}. ` +
      'Los pedidos van a seguir mostrando qué se vendió y a cuánto, pero dejan de estar asociados ' +
      'a este producto: tus reportes por producto pierden esas ventas.',
    );
  }
  if (dep.publicaciones > 0) {
    consecuencias.push(
      `Está publicado en ${plural(dep.publicaciones, 'marketplace', 'marketplaces')}. ` +
      'Se pierde el vínculo con esa publicación: queda viva allá y Smarty deja de reconocerla.',
    );
  }
  if (dep.movimientos > 0) {
    consecuencias.push(
      `Se borra su historial de inventario (${plural(dep.movimientos, 'movimiento', 'movimientos')}).`,
    );
  }

  // La ficha técnica se pierde SIEMPRE, tenga historial o no. Vale decirlo igual: es el trabajo de
  // cargar características, y se va sin dejar rastro.
  consecuencias.push('Se borra su ficha técnica, sus fotos, sus variantes y sus categorías.');

  const conHistorial = dep.ventas > 0 || dep.publicaciones > 0 || dep.movimientos > 0;

  return {
    nivel: conHistorial ? 'con_historial' : 'libre',
    consecuencias,
    exigeEscribirNombre: conHistorial,
  };
}

/**
 * ¿El texto que escribió alcanza para confirmar? Se compara sin distinguir mayúsculas ni espacios
 * de más: se pide leer el nombre, no transcribirlo con precisión de notario.
 */
export function nombreConfirmado(escrito: string, nombreReal: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  return norm(escrito) !== '' && norm(escrito) === norm(nombreReal);
}
